import React, { useState, useEffect } from 'react';
import Sidebar from './Sidebar';
import DashboardOverview from './DashboardOverview';
import OrdersView from './OrdersView';
import CityMapView from './CityMapView';
import EmployeesView from './EmployeesView';
import PrinterSettings from '../PrinterSettings';
import DailyGoalProgress from '../DailyGoalProgress';
import TenantSettingsManager from '../TenantSettingsManager';
import { Dialog, DialogContent } from '@/components/ui/dialog';
import { ArrowLeft } from 'lucide-react';
import { BackButton } from '@/components/ui/back-button';

// Placeholder components for sections not yet fully implemented
const POSPlaceholder = ({ onNavigate }) => (
  <div className="p-6 h-full flex items-center justify-center">
    <div className="text-center">
      <div className="w-20 h-20 mx-auto mb-4 rounded-2xl bg-emerald-500/20 flex items-center justify-center">
        <svg className="w-10 h-10 text-emerald-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-2.293 2.293c-.63.63-.184 1.707.707 1.707H17m0 0a2 2 0 100 4 2 2 0 000-4zm-8 2a2 2 0 11-4 0 2 2 0 014 0z" />
        </svg>
      </div>
      <h2 className="text-2xl font-bold text-white mb-2">Kasse</h2>
      <p className="text-gray-400 mb-4">Neue Bestellung aufnehmen</p>
      <button 
        onClick={() => onNavigate && onNavigate('pos')}
        className="px-6 py-3 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg font-medium"
      >
        Zur Kasse wechseln
      </button>
    </div>
  </div>
);

const CashbookPlaceholder = () => (
  <div className="p-6 h-full flex items-center justify-center">
    <div className="text-center">
      <div className="w-20 h-20 mx-auto mb-4 rounded-2xl bg-rose-500/20 flex items-center justify-center">
        <svg className="w-10 h-10 text-rose-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      </div>
      <h2 className="text-2xl font-bold text-white mb-2">Kassenbuch</h2>
      <p className="text-gray-400 mb-4">Tagesabschluss, Z-Bon, Kassenstand</p>
      <p className="text-gray-500 text-sm">Kommt in Phase 2</p>
    </div>
  </div>
);

const CustomersPlaceholder = () => (
  <div className="p-6 h-full flex items-center justify-center">
    <div className="text-center">
      <div className="w-20 h-20 mx-auto mb-4 rounded-2xl bg-cyan-500/20 flex items-center justify-center">
        <svg className="w-10 h-10 text-cyan-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
        </svg>
      </div>
      <h2 className="text-2xl font-bold text-white mb-2">Kunden</h2>
      <p className="text-gray-400 mb-4">Kundendatenbank & Bestellhistorie</p>
      <p className="text-gray-500 text-sm">Kommt in Phase 2</p>
    </div>
  </div>
);

const SettingsPlaceholder = ({ onOpenPrinters, onOpenIntegrations, onOpenBusinessSettings, onBack }) => (
  <div className="p-6">
    <div className="flex items-center gap-4 mb-6">
      {onBack && (
        <BackButton 
          onClick={onBack} 
          label="Zurück" 
          className="text-gray-400 hover:text-white"
        />
      )}
      <h1 className="text-2xl font-bold text-white">Einstellungen</h1>
    </div>
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
      {/* Geschäftseinstellungen - NEU */}
      <button
        onClick={onOpenBusinessSettings}
        className="p-6 bg-[#12121a] border border-gray-800 rounded-xl text-left hover:border-teal-500/50 transition-colors group"
        data-testid="settings-business-btn"
      >
        <div className="w-12 h-12 rounded-xl bg-teal-500/20 flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
          <svg className="w-6 h-6 text-teal-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
          </svg>
        </div>
        <h3 className="text-white font-medium mb-1">Geschäft</h3>
        <p className="text-gray-500 text-sm">Funktionen, Rabatte & mehr</p>
      </button>
      
      <button
        onClick={onOpenPrinters}
        className="p-6 bg-[#12121a] border border-gray-800 rounded-xl text-left hover:border-amber-500/50 transition-colors group"
        data-testid="settings-printers-btn"
      >
        <div className="w-12 h-12 rounded-xl bg-amber-500/20 flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
          <svg className="w-6 h-6 text-amber-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z" />
          </svg>
        </div>
        <h3 className="text-white font-medium mb-1">Drucker</h3>
        <p className="text-gray-500 text-sm">Bon-Drucker & Küchen-Drucker</p>
      </button>
      
      <button
        onClick={onOpenIntegrations}
        className="p-6 bg-[#12121a] border border-gray-800 rounded-xl text-left hover:border-blue-500/50 transition-colors group"
        data-testid="settings-integrations-btn"
      >
        <div className="w-12 h-12 rounded-xl bg-blue-500/20 flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
          <svg className="w-6 h-6 text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9a9 9 0 01-9-9m9 9c1.657 0 3-4.03 3-9s-1.343-9-3-9m0 18c-1.657 0-3-4.03-3-9s1.343-9 3-9m-9 9a9 9 0 019-9" />
          </svg>
        </div>
        <h3 className="text-white font-medium mb-1">Integrationen</h3>
        <p className="text-gray-500 text-sm">Lieferando, Wolt, Website</p>
      </button>
    </div>
  </div>
);

export default function MainDashboard({ 
  sessionToken, 
  account, 
  onLogout, 
  onStationChange,
  onOpenPrinters,
  onOpenIntegrations,
  onSwitchToPOS,
  onOpenDeliveryPickup
}) {
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [activeSection, setActiveSection] = useState('dashboard');
  const [showPrinterModal, setShowPrinterModal] = useState(false);
  const [showIntegrationsModal, setShowIntegrationsModal] = useState(false);
  const [showBusinessSettingsModal, setShowBusinessSettingsModal] = useState(false);

  // Handle section change
  const handleSectionChange = (section) => {
    if (section === 'pos') {
      // Switch to full POS mode
      if (onSwitchToPOS) {
        onSwitchToPOS();
      }
    } else {
      setActiveSection(section);
    }
  };

  // Handle back to dashboard
  const handleBackToDashboard = () => {
    setActiveSection('dashboard');
  };

  // Render active section content
  const renderContent = () => {
    switch (activeSection) {
      case 'dashboard':
        return <DashboardOverview sessionToken={sessionToken} onOpenDeliveryPickup={onOpenDeliveryPickup} />;
      case 'orders':
        return <OrdersView sessionToken={sessionToken} onBack={handleBackToDashboard} />;
      case 'map':
        return <CityMapView sessionToken={sessionToken} businessAddress={account?.address} />;
      case 'employees':
        return <EmployeesView sessionToken={sessionToken} />;
      case 'pos':
        return <POSPlaceholder onNavigate={onSwitchToPOS} />;
      case 'cashbook':
        return <CashbookPlaceholder />;
      case 'customers':
        return <CustomersPlaceholder />;
      case 'settings':
        return <SettingsPlaceholder 
          onOpenPrinters={() => setShowPrinterModal(true)} 
          onOpenIntegrations={() => setShowIntegrationsModal(true)} 
          onOpenBusinessSettings={() => setShowBusinessSettingsModal(true)}
          onBack={handleBackToDashboard} 
        />;
      default:
        return <DashboardOverview sessionToken={sessionToken} />;
    }
  };

  return (
    <div className="flex h-screen bg-[#0a0a0f] overflow-hidden">
      {/* Sidebar */}
      <Sidebar
        collapsed={sidebarCollapsed}
        onToggle={() => setSidebarCollapsed(!sidebarCollapsed)}
        activeSection={activeSection}
        onSectionChange={handleSectionChange}
        businessName={account?.business_name}
        onLogout={onLogout}
        onStationChange={onStationChange}
      />
      
      {/* Main Content */}
      <main className="flex-1 overflow-auto">
        {renderContent()}
      </main>

      {/* Printer Settings Modal */}
      <Dialog open={showPrinterModal} onOpenChange={setShowPrinterModal}>
        <DialogContent className="bg-[#12121a] border-gray-800 text-white max-w-4xl max-h-[85vh] overflow-y-auto">
          <PrinterSettings 
            sessionToken={sessionToken} 
            onClose={() => setShowPrinterModal(false)} 
          />
        </DialogContent>
      </Dialog>

      {/* Integrations Modal Placeholder */}
      <Dialog open={showIntegrationsModal} onOpenChange={setShowIntegrationsModal}>
        <DialogContent className="bg-[#12121a] border-gray-800 text-white max-w-2xl">
          <div className="text-center py-8">
            <div className="w-16 h-16 mx-auto mb-4 rounded-xl bg-blue-500/20 flex items-center justify-center">
              <svg className="w-8 h-8 text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9a9 9 0 01-9-9m9 9c1.657 0 3-4.03 3-9s-1.343-9-3-9m0 18c-1.657 0-3-4.03-3-9s1.343-9 3-9m-9 9a9 9 0 019-9" />
              </svg>
            </div>
            <h2 className="text-xl font-bold text-white mb-2">API Integrationen</h2>
            <p className="text-gray-400">Für Integrationen wechseln Sie bitte zur Kasse-Ansicht</p>
          </div>
        </DialogContent>
      </Dialog>

      {/* Business Settings Modal */}
      <Dialog open={showBusinessSettingsModal} onOpenChange={setShowBusinessSettingsModal}>
        <DialogContent className="bg-[#0a0a0f] border-gray-800 text-white max-w-4xl max-h-[90vh] overflow-y-auto p-0">
          <TenantSettingsManager onBack={() => setShowBusinessSettingsModal(false)} />
        </DialogContent>
      </Dialog>
    </div>
  );
}
